import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Copy, Trash2, ExternalLink, Pencil, LogOut, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const STATUS_LABEL = {
  draft: { label: "Brouillon", cls: "bg-[#EAE8E3] text-[#737373]" },
  sent: { label: "Envoyé", cls: "bg-[#96836B] text-white" },
  accepted: { label: "Accepté", cls: "bg-[#1A1B18] text-white" },
  expired: { label: "Expiré", cls: "bg-[#F4E0D9] text-[#7A4B3B]" },
};

export default function AdminProposals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/proposals");
      setItems(data);
    } catch {
      toast.error("Impossible de charger les devis.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/admin/proposals", { title: "Nouveau devis" });
      navigate(`/admin/proposals/${data.id}`);
    } catch {
      toast.error("Création impossible.");
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/admin/proposals/${id}/duplicate`);
      toast.success("Devis dupliqué.");
      setItems((s) => [data, ...s]);
    } catch {
      toast.error("Duplication impossible.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce devis définitivement ?")) return;
    try {
      await api.delete(`/admin/proposals/${id}`);
      setItems((s) => s.filter((p) => p.id !== id));
      toast.success("Devis supprimé.");
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/devis/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié dans le presse-papier.");
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Nav />
      <main className="pt-32 pb-24 px-6 md:px-12 max-w-[1400px] mx-auto">
        <header className="flex items-end justify-between flex-wrap gap-6 border-b border-[#EAE8E3] pb-8">
          <div>
            <p className="label-eyebrow">Back-office</p>
            <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Devis premium</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[11px] uppercase tracking-[0.24em] text-[#737373] hover:text-[#1A1B18]">
              ← Demandes reçues
            </Link>
            <Button
              data-testid="btn-new-proposal"
              onClick={create}
              className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white px-6 py-5 text-[11px] uppercase tracking-[0.28em]"
            >
              <Plus className="h-4 w-4 mr-2" /> Nouveau devis
            </Button>
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="text-[#737373] hover:text-[#1A1B18]" title={user?.email}>
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading ? (
            <p className="text-sm text-[#737373]">Chargement…</p>
          ) : items.length === 0 ? (
            <div className="col-span-full text-center py-24">
              <Sparkles className="h-10 w-10 text-[#96836B] mx-auto" strokeWidth={1.2} />
              <p className="font-serif-display text-2xl mt-4">Votre premier devis premium</p>
              <p className="text-sm text-[#737373] mt-2">Cliquez sur "Nouveau devis" pour démarrer.</p>
            </div>
          ) : (
            items.map((p) => {
              const status = STATUS_LABEL[p.status] || STATUS_LABEL.draft;
              return (
                <article key={p.id} data-testid={`proposal-card-${p.id}`} className="bg-white border border-[#EAE8E3] p-6 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <p className="font-serif-display text-2xl leading-tight">{p.title}</p>
                    <span className={`text-[10px] px-2 py-1 ${status.cls}`}>{status.label}</span>
                  </div>
                  {p.destination && <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">{p.destination}</p>}
                  {p.client_name && <p className="text-sm text-[#1A1B18] mt-3">Pour {p.client_name}</p>}
                  <p className="text-xs text-[#737373] mt-1">
                    Modifié le {format(parseISO(p.updated_at), "d MMM yyyy", { locale: fr })}
                  </p>
                  {p.total_deal_price && (
                    <p className="text-base text-[#1A1B18] mt-3">
                      {Number(p.total_deal_price).toLocaleString("fr-FR")} {p.currency}
                    </p>
                  )}
                  <div className="mt-6 pt-4 border-t border-[#EAE8E3] flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={() => navigate(`/admin/proposals/${p.id}`)}
                      data-testid={`btn-edit-${p.id}`}
                      className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white text-[10px] uppercase tracking-[0.2em] py-2 px-4 h-auto"
                    >
                      <Pencil className="h-3 w-3 mr-1.5" /> Éditer
                    </Button>
                    <button onClick={() => copyLink(p.slug)} title="Copier le lien" className="text-[#737373] hover:text-[#1A1B18] p-2">
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    <button onClick={() => duplicate(p.id)} title="Dupliquer" className="text-[#737373] hover:text-[#1A1B18] p-2">
                      <Copy className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(p.id)} title="Supprimer" className="text-[#737373] hover:text-[#7A4B3B] p-2 ml-auto">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}
